import type { ReactElement, SVGProps } from 'react';

/**
 * The icon set.
 *
 * Hand-rolled rather than pulled from a package: the whole app needs about
 * thirty glyphs, and an icon library would add a dependency, a bundle and a
 * licence for something a single file covers. Every icon is a 24-unit
 * stroke drawing that inherits currentColor and sizes from CSS, so one
 * definition works on the dark rail, inside a button and in an empty state.
 */

type Props = SVGProps<SVGSVGElement>;

function Svg({ children, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ---------- navigation ---------- */

export const IconDashboard = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7.5" height="8.5" rx="1.6" />
    <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.6" />
    <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.6" />
    <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.6" />
  </Svg>
);

export const IconCollect = (p: Props) => (
  <Svg {...p}>
    <path d="M2.5 8.5A2.5 2.5 0 0 1 5 6h14a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 19 18H5a2.5 2.5 0 0 1-2.5-2.5z" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 10v4M18 10v4" />
  </Svg>
);

export const IconList = (p: Props) => (
  <Svg {...p}>
    <path d="M8.5 6h12M8.5 12h12M8.5 18h12" />
    <path d="M3.6 6h.01M3.6 12h.01M3.6 18h.01" strokeWidth={2.4} />
  </Svg>
);

export const IconReceipt = (p: Props) => (
  <Svg {...p}>
    <path d="M5 2.8h14v18.4l-2.8-1.8-2.8 1.8-2.8-1.8-2.8 1.8L5 19.4z" />
    <path d="M9 8h6M9 12h6" />
  </Svg>
);

export const IconSearch = (p: Props) => (
  <Svg {...p}>
    <circle cx="10.8" cy="10.8" r="6.8" />
    <path d="m20 20-4.4-4.4" />
  </Svg>
);

export const IconCalendarPlus = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2.2" />
    <path d="M3 10h18M8 3v4M16 3v4M12 13.5v4M10 15.5h4" />
  </Svg>
);

export const IconStudents = (p: Props) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.6" />
    <path d="M2.6 20c0-3.5 2.9-5.8 6.4-5.8s6.4 2.3 6.4 5.8" />
    <path d="M17 4.6a3.6 3.6 0 0 1 0 6.9M18.4 14.6c1.9.6 3 2.2 3 4.4" />
  </Svg>
);

export const IconTeacher = (p: Props) => (
  <Svg {...p}>
    <path d="m12 3.2 9.2 4.6L12 12.4 2.8 7.8z" />
    <path d="M6.4 10v5.2c0 1.7 2.5 3 5.6 3s5.6-1.3 5.6-3V10" />
    <path d="M21.2 7.8v6" />
  </Svg>
);

export const IconStaff = (p: Props) => (
  <Svg {...p}>
    <rect x="2.6" y="7" width="18.8" height="13.4" rx="2.2" />
    <path d="M8.6 7V5.2a2 2 0 0 1 2-2h2.8a2 2 0 0 1 2 2V7" />
    <path d="M2.6 12.6h18.8M11 12v2" />
  </Svg>
);

export const IconCourses = (p: Props) => (
  <Svg {...p}>
    <path d="M12 6.6C10.4 5.1 8.3 4.4 5 4.4c-.9 0-1.6.1-2.2.2v13.6c.6-.1 1.3-.2 2.2-.2 3.3 0 5.4.7 7 2.2" />
    <path d="M12 6.6c1.6-1.5 3.7-2.2 7-2.2.9 0 1.6.1 2.2.2v13.6c-.6-.1-1.3-.2-2.2-.2-3.3 0-5.4.7-7 2.2z" />
    <path d="M12 6.6v13.6" />
  </Svg>
);

export const IconTests = (p: Props) => (
  <Svg {...p}>
    <path d="M8.6 3.4h6.8a1.6 1.6 0 0 1 1.6 1.6v.6h1.4A1.6 1.6 0 0 1 20 7.2v12.2a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 19.4V7.2a1.6 1.6 0 0 1 1.6-1.6H7V5a1.6 1.6 0 0 1 1.6-1.6z" />
    <path d="m8.6 13.4 2 2 4-4.2" />
  </Svg>
);

export const IconBanknote = (p: Props) => (
  <Svg {...p}>
    <path d="M12 2.6v18.8" />
    <path d="M16.4 6.4H9.8a3.1 3.1 0 0 0 0 6.2h4.4a3.1 3.1 0 0 1 0 6.2H7" />
  </Svg>
);

export const IconWallet = (p: Props) => (
  <Svg {...p}>
    <path d="M3 7.4A2.4 2.4 0 0 1 5.4 5h11.2A2.4 2.4 0 0 1 19 7.4V9" />
    <path d="M3 7.4v9.2A2.4 2.4 0 0 0 5.4 19h13.2a2.4 2.4 0 0 0 2.4-2.4v-5.2a2.4 2.4 0 0 0-2.4-2.4H5.4A2.4 2.4 0 0 1 3 6.6" />
    <circle cx="16.6" cy="14" r="1.2" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconExpense = (p: Props) => (
  <Svg {...p}>
    <rect x="2.6" y="5" width="18.8" height="14" rx="2.4" />
    <path d="M2.6 9.6h18.8" />
    <path d="M6.4 14.6h3.2" />
  </Svg>
);

export const IconChart = (p: Props) => (
  <Svg {...p}>
    <path d="M3.4 20.6h17.2" />
    <path d="M6.6 20.6v-6.4M11.4 20.6V6.4M16.2 20.6v-9.6" />
  </Svg>
);

export const IconUsers = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="7.6" r="3.8" />
    <path d="M4.8 20.4c0-3.9 3.2-6.4 7.2-6.4s7.2 2.5 7.2 6.4" />
  </Svg>
);

export const IconBuilding = (p: Props) => (
  <Svg {...p}>
    <path d="M4 21V5.6a2 2 0 0 1 2-2h7.2a2 2 0 0 1 2 2V21" />
    <path d="M15.2 10h3a2 2 0 0 1 2 2v9M2.6 21h18.8" />
    <path d="M8 8h3.2M8 12.4h3.2M8 16.8h3.2" />
  </Svg>
);

export const IconUser = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.6 20.4c0-4.1 3.3-6.6 7.4-6.6s7.4 2.5 7.4 6.6" />
  </Svg>
);

/* ---------- interface ---------- */

export const IconChevronRight = (p: Props) => (
  <Svg {...p}><path d="m9 5 7 7-7 7" /></Svg>
);

export const IconChevronLeft = (p: Props) => (
  <Svg {...p}><path d="m15 5-7 7 7 7" /></Svg>
);

export const IconChevronsLeft = (p: Props) => (
  <Svg {...p}><path d="m11 5-7 7 7 7M19 5l-7 7 7 7" /></Svg>
);

export const IconChevronsRight = (p: Props) => (
  <Svg {...p}><path d="m13 5 7 7-7 7M5 5l7 7-7 7" /></Svg>
);

export const IconCheck = (p: Props) => (
  <Svg {...p} strokeWidth={2.6}><path d="m4.5 12.5 5 5 10-11" /></Svg>
);

export const IconX = (p: Props) => (
  <Svg {...p} strokeWidth={2.2}><path d="M5.5 5.5l13 13M18.5 5.5l-13 13" /></Svg>
);

export const IconAlert = (p: Props) => (
  <Svg {...p}>
    <path d="M10.3 3.9 2.4 17.4a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9.4v4.2" strokeWidth={2.2} />
    <path d="M12 17.3h.01" strokeWidth={2.4} />
  </Svg>
);

export const IconInfo = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9.2" />
    <path d="M12 11.2v5" strokeWidth={2.2} />
    <path d="M12 7.9h.01" strokeWidth={2.4} />
  </Svg>
);

export const IconSun = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.4v2.2M12 19.4v2.2M4.2 12H2M22 12h-2.2M6.5 6.5 4.9 4.9M19.1 19.1l-1.6-1.6M17.5 6.5l1.6-1.6M4.9 19.1l1.6-1.6" />
  </Svg>
);

export const IconMoon = (p: Props) => (
  <Svg {...p}>
    <path d="M20.4 14.4A8.7 8.7 0 0 1 9.6 3.6a8.8 8.8 0 1 0 10.8 10.8z" />
  </Svg>
);

export const IconMenu = (p: Props) => (
  <Svg {...p} strokeWidth={2}><path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" /></Svg>
);

export const IconPanelLeft = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="4.4" width="18" height="15.2" rx="2.2" />
    <path d="M9.6 4.4v15.2" />
  </Svg>
);

export const IconLogOut = (p: Props) => (
  <Svg {...p}>
    <path d="M14.4 20H6.2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8.2" />
    <path d="m17.6 15.6 3.6-3.6-3.6-3.6M21.2 12H9.6" />
  </Svg>
);

export const IconPlus = (p: Props) => (
  <Svg {...p} strokeWidth={2.2}><path d="M12 5.2v13.6M5.2 12h13.6" /></Svg>
);

export const IconRefresh = (p: Props) => (
  <Svg {...p}>
    <path d="M20.4 11.2a8.5 8.5 0 1 0-.6 4.6" />
    <path d="M20.8 4.6v6h-6" />
  </Svg>
);

export const IconInbox = (p: Props) => (
  <Svg {...p}>
    <path d="M21 13.6V18a2.4 2.4 0 0 1-2.4 2.4H5.4A2.4 2.4 0 0 1 3 18v-4.4" />
    <path d="M3 13.6 5.9 5a2.4 2.4 0 0 1 2.3-1.6h7.6A2.4 2.4 0 0 1 18.1 5L21 13.6h-5.2a1 1 0 0 0-.95.68l-.3.9a1 1 0 0 1-.95.68h-3.2a1 1 0 0 1-.95-.68l-.3-.9a1 1 0 0 0-.95-.68z" />
  </Svg>
);

export const IconLock = (p: Props) => (
  <Svg {...p}>
    <rect x="4.4" y="10.2" width="15.2" height="10.4" rx="2.2" />
    <path d="M8.2 10.2V7.4a3.8 3.8 0 0 1 7.6 0v2.8" />
  </Svg>
);

export const IconEye = (p: Props) => (
  <Svg {...p}>
    <path d="M2 12s3.8-6.4 10-6.4S22 12 22 12s-3.8 6.4-10 6.4S2 12 2 12z" />
    <circle cx="12" cy="12" r="2.9" />
  </Svg>
);

export const IconEyeOff = (p: Props) => (
  <Svg {...p}>
    <path d="M9.6 5.9a9.9 9.9 0 0 1 2.4-.3c6.2 0 10 6.4 10 6.4a17 17 0 0 1-2.9 3.6" />
    <path d="M6.5 7.5A16.6 16.6 0 0 0 2 12s3.8 6.4 10 6.4a9.9 9.9 0 0 0 3.9-.8" />
    <path d="M9.9 9.9a2.9 2.9 0 0 0 4.1 4.1" />
    <path d="m3.5 3.5 17 17" />
  </Svg>
);

export const IconShield = (p: Props) => (
  <Svg {...p}>
    <path d="M12 2.8 4.6 6v5.6c0 4.6 3.1 8.4 7.4 9.6 4.3-1.2 7.4-5 7.4-9.6V6z" />
    <path d="m9 12 2.2 2.2L15.4 10" />
  </Svg>
);

export const IconClock = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9.2" />
    <path d="M12 6.8V12l3.4 2" />
  </Svg>
);

export const IconArrowRight = (p: Props) => (
  <Svg {...p}><path d="M4.4 12h15.2M13.6 6l6 6-6 6" /></Svg>
);

export const IconTrash = (p: Props) => (
  <Svg {...p}>
    <path d="M4 6.4h16M9.4 6.4V4.6a1.4 1.4 0 0 1 1.4-1.4h2.4a1.4 1.4 0 0 1 1.4 1.4v1.8" />
    <path d="M6.4 6.4 7.3 19a1.8 1.8 0 0 0 1.8 1.6h5.8a1.8 1.8 0 0 0 1.8-1.6l.9-12.6" />
    <path d="M10.4 10.6v5.6M13.6 10.6v5.6" />
  </Svg>
);

export const IconPencil = (p: Props) => (
  <Svg {...p}>
    <path d="M15.6 4.2 19.8 8.4 8.2 20H4v-4.2z" />
    <path d="m13.4 6.4 4.2 4.2" />
  </Svg>
);

export const IconDownload = (p: Props) => (
  <Svg {...p}>
    <path d="M12 3.4v11.2M7.6 10.6 12 15l4.4-4.4" />
    <path d="M4 17.6v1.4a1.6 1.6 0 0 0 1.6 1.6h12.8a1.6 1.6 0 0 0 1.6-1.6v-1.4" />
  </Svg>
);

export const IconUpload = (p: Props) => (
  <Svg {...p}>
    <path d="M12 15.4V4.2M7.6 8.2 12 3.8l4.4 4.4" />
    <path d="M4 17.6v1.4a1.6 1.6 0 0 0 1.6 1.6h12.8a1.6 1.6 0 0 0 1.6-1.6v-1.4" />
  </Svg>
);

export const IconTrend = (p: Props) => (
  <Svg {...p}>
    <path d="m3.4 16.4 5.4-5.4 3.6 3.6 7.2-7.2" />
    <path d="M15.2 7.4h5.4v5.4" />
  </Svg>
);

/** Maps a nav path to its glyph. Kept beside the icons so adding a screen is one edit. */
export const NAV_ICONS: Record<string, (p: Props) => ReactElement> = {
  '/': IconDashboard,
  '/fees/submission': IconCollect,
  '/fees': IconList,
  '/fees/slips': IconReceipt,
  '/fees/search': IconSearch,
  '/fees/advance': IconCalendarPlus,
  '/students': IconStudents,
  '/teachers': IconTeacher,
  '/staff': IconStaff,
  '/courses': IconCourses,
  '/tests': IconTests,
  '/salary/teachers': IconBanknote,
  '/salary/staff': IconWallet,
  '/expenses': IconExpense,
  '/expenses/summary': IconChart,
  '/users': IconUsers,
  '/master-information': IconBuilding,
  '/me/info': IconUser,
  '/me/courses': IconCourses,
  '/me/fees': IconReceipt,
  '/me/scores': IconTests,
  '/me/teaching': IconTeacher,
};
